import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Badge, Breadcrumbs, Card, ConfidenceMeter, Drawer, Empty, ErrorNotice, Loading, Modal,
  Notice, Pager, fmt, fmtDate, useToast, vehicleLabel,
} from '../components/ui.jsx';

const LIMIT = 25;

const ATTRIBUTE_LABELS = {
  make: 'Make', model: 'Model', variant: 'Variant',
  capacity: 'Capacity', fuel: 'Fuel type', seating: 'Seating',
};

export default function MappingsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    insurerId: '', status: 'pending', band: '', ratingCheck: '', search: '', sort: 'risk',
  });
  const [offset, setOffset] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [remapFor, setRemapFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const insurers = useQuery({ queryKey: ['insurers'], queryFn: () => api.listInsurers().then((r) => r.data) });

  const query = useQuery({
    queryKey: ['mappings', filters, offset],
    queryFn: () => api.listMappings({ ...filters, limit: LIMIT, offset }),
  });

  const rows = query.data?.data || [];
  const total = query.data?.meta?.total || 0;
  const selected = selectedIndex === null ? null : rows[selectedIndex];

  const detail = useQuery({
    queryKey: ['mapping', selected?.id],
    queryFn: () => api.getMapping(selected.id).then((r) => r.data),
    enabled: Boolean(selected?.id),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mappings'] });
    queryClient.invalidateQueries({ queryKey: ['overview'] });
    queryClient.invalidateQueries({ queryKey: ['mapping', selected?.id] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ id, body }) => api.approve(id, body),
    onSuccess: () => { toast.success('Mapping approved'); invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const rejectMutation = useMutation({
    mutationFn: ({ id, body }) => api.reject(id, body),
    onSuccess: () => { toast.success('Mapping rejected'); invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const remapMutation = useMutation({
    mutationFn: ({ id, body }) => api.remap(id, body),
    onSuccess: () => { toast.success('Record remapped'); setRemapFor(null); invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  // Arrow keys step through records while the detail panel is open.
  useEffect(() => {
    if (selectedIndex === null) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') setSelectedIndex(null);
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(rows.length - 1, i + 1));
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIndex, rows.length]);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);
    setSelectedIndex(null);
  };

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Review queue' }]} />
      <div className="page-head">
        <div>
          <h1>Review queue</h1>
          <div className="page-sub">
            Each row is one insurer record and the GIIB vehicle the engine chose for it. Rows failing the
            premium band check are listed first, because a wrong link there changes the premium.
          </div>
        </div>
        <div className="spacer" />
        {can('reviewer') && (
          <button className="btn btn-primary" onClick={() => setBulkOpen(true)}>Bulk approve</button>
        )}
      </div>

      <Card bodyClass="tight">
        <div className="filters">
          <div className="f grow">
            <label htmlFor="search">Search</label>
            <input
              id="search" placeholder="Insurer code, universal key, make or model"
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
            />
          </div>
          <div className="f">
            <label htmlFor="f-insurer">Insurer</label>
            <select id="f-insurer" value={filters.insurerId} onChange={(e) => setFilter('insurerId', e.target.value)}>
              <option value="">All</option>
              {(insurers.data || []).map((i) => <option key={i.id} value={i.id}>{i.shortCode}</option>)}
            </select>
          </div>
          <div className="f">
            <label htmlFor="f-status">Status</label>
            <select id="f-status" value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="f-band">Confidence</label>
            <select id="f-band" value={filters.band} onChange={(e) => setFilter('band', e.target.value)}>
              <option value="">All</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="none">No match</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="f-rating">Band check</label>
            <select id="f-rating" value={filters.ratingCheck} onChange={(e) => setFilter('ratingCheck', e.target.value)}>
              <option value="">All</option>
              <option value="fail">Failed</option>
              <option value="warn">Warning</option>
              <option value="pass">Passed</option>
              <option value="na">Not applicable</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="f-sort">Sort</label>
            <select id="f-sort" value={filters.sort} onChange={(e) => setFilter('sort', e.target.value)}>
              <option value="risk">Risk first</option>
              <option value="confidence_desc">Confidence, high to low</option>
              <option value="confidence_asc">Confidence, low to high</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>

        {query.isLoading ? <div style={{ padding: 16 }}><Loading rows={6} /></div>
          : query.error ? <div style={{ padding: 16 }}><ErrorNotice error={query.error} /></div>
            : rows.length === 0 ? <Empty title="Nothing matches these filters">Clear a filter, or import an insurer master.</Empty>
              : (
                <>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Insurer record</th>
                          <th>Matched GIIB vehicle</th>
                          <th style={{ minWidth: 150 }}>Confidence</th>
                          <th>Band check</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((m, i) => (
                          <tr
                            key={m.id}
                            className={`clickable ${selectedIndex === i ? 'selected' : ''}`}
                            onClick={() => setSelectedIndex(i)}
                          >
                            <td>
                              <div className="cell-main">
                                {[m.icRecord.make, m.icRecord.model, m.icRecord.variant].filter(Boolean).join(' ')}
                              </div>
                              <div className="cell-sub">
                                {m.insurer.code} code <strong>{m.icRecord.code}</strong>
                                {m.icRecord.codeColumn && <span className="prov"> · from {m.icRecord.codeColumn}</span>}
                                {m.icRecord.cubicCapacity ? ` · ${m.icRecord.cubicCapacity} cc` : ''}
                              </div>
                            </td>
                            <td>
                              {m.giibVehicle ? (
                                <>
                                  <div className="cell-main">{vehicleLabel(m.giibVehicle)}</div>
                                  <div className="cell-sub mono">
                                    {m.giibVehicle.universalKey}
                                    {m.giibVehicle.cubicCapacity ? ` · ${m.giibVehicle.cubicCapacity} cc` : ''}
                                  </div>
                                </>
                              ) : <span className="prov">no candidate found</span>}
                            </td>
                            <td><ConfidenceMeter value={m.confidence} band={m.band} /></td>
                            <td><Badge kind={m.ratingCheck}>{m.ratingCheck}</Badge></td>
                            <td>
                              <Badge kind={m.status}>{m.status}</Badge>
                              {m.autoApproved && <div className="prov" style={{ marginTop: 2 }}>auto</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
                </>
              )}
      </Card>

      {selected && (
        <MappingDrawer
          mapping={detail.data || selected}
          loading={detail.isLoading}
          position={{ index: offset + selectedIndex + 1, total }}
          onClose={() => setSelectedIndex(null)}
          onPrev={selectedIndex > 0 ? () => setSelectedIndex(selectedIndex - 1) : null}
          onNext={selectedIndex < rows.length - 1 ? () => setSelectedIndex(selectedIndex + 1) : null}
          canReview={can('reviewer')}
          onApprove={(body) => approveMutation.mutate({ id: selected.id, body })}
          onReject={(body) => rejectMutation.mutate({ id: selected.id, body })}
          onRemapRequest={() => setRemapFor(detail.data || selected)}
          busy={approveMutation.isPending || rejectMutation.isPending}
        />
      )}

      {remapFor && (
        <RemapModal
          mapping={remapFor}
          onClose={() => setRemapFor(null)}
          onSubmit={(body) => remapMutation.mutate({ id: remapFor.id, body })}
          busy={remapMutation.isPending}
        />
      )}

      {bulkOpen && (
        <BulkApproveModal
          insurers={insurers.data || []}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); invalidate(); }}
        />
      )}
    </div>
  );
}

/* ------------------------------- drawer ------------------------------- */

function MappingDrawer({
  mapping, loading, position, onClose, onPrev, onNext, canReview,
  onApprove, onReject, onRemapRequest, busy,
}) {
  const [comment, setComment] = useState('');
  const [override, setOverride] = useState(false);
  const ic = mapping.icRecord;
  const giib = mapping.giibVehicle;

  useEffect(() => { setComment(''); setOverride(false); }, [mapping.id]);

  const rows = [
    ['Make', ic.make, giib?.make],
    ['Model', ic.model, giib?.model],
    ['Variant', ic.variant, giib?.variant],
    ['Fuel type', ic.fuelType, giib?.fuelType],
    ['Cubic capacity', ic.cubicCapacity, giib?.cubicCapacity],
    ['Power (kW)', ic.kilowatt, giib?.kilowatt],
    ['Seating', ic.seatingCapacity, giib?.seatingCapacity],
  ];

  return (
    <Drawer
      title={`${ic.make || ''} ${ic.model || ''}`.trim() || 'Insurer record'}
      subtitle={`${mapping.insurer.name} · code ${ic.code}${ic.codeColumn ? ` · read from column ${ic.codeColumn}` : ''}`}
      onClose={onClose}
      nav={(
        <div className="drawer-nav">
          <button className="btn btn-sm btn-ghost" onClick={onPrev} disabled={!onPrev} aria-label="Previous record">‹</button>
          <span>Record {fmt(position.index)} of {fmt(position.total)}</span>
          <button className="btn btn-sm btn-ghost" onClick={onNext} disabled={!onNext} aria-label="Next record">›</button>
        </div>
      )}
      footer={canReview ? (
        <>
          <button
            className="btn btn-primary"
            disabled={busy || !giib || mapping.status === 'approved'}
            onClick={() => onApprove({ comment: comment || undefined, overrideRating: override })}
          >
            Approve
          </button>
          <button className="btn" disabled={busy} onClick={onRemapRequest}>Remap to another vehicle</button>
          <button
            className="btn btn-danger"
            disabled={busy || comment.trim().length < 5}
            onClick={() => onReject({ comment })}
            title={comment.trim().length < 5 ? 'A reason of at least 5 characters is required' : undefined}
          >
            Reject
          </button>
          {mapping.ratingCheck === 'fail' && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
              Override the failed band check
            </label>
          )}
        </>
      ) : <span className="prov">Read-only access - reviewing requires the reviewer role.</span>}
    >
      {loading && <Loading rows={3} />}

      {mapping.ratingCheck === 'fail' && (
        <Notice tone="error" title="Premium band mismatch">{mapping.ratingNote}</Notice>
      )}
      {mapping.ratingCheck === 'warn' && (
        <Notice tone="warn" title="Band check inconclusive">{mapping.ratingNote}</Notice>
      )}
      {mapping.conflictOf && (
        <Notice tone="warn" title="The engine disagrees with the stored link">{mapping.comment}</Notice>
      )}

      <div className="tiles" style={{ marginBottom: 14 }}>
        <div className="tile">
          <div className="tile-label">Confidence</div>
          <div style={{ marginTop: 8 }}><ConfidenceMeter value={mapping.confidence} band={mapping.band} /></div>
          <div className="tile-foot">
            {mapping.margin !== null && mapping.margin !== undefined
              ? `${mapping.margin} points ahead of the runner-up`
              : 'No runner-up to compare against'}
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Decision</div>
          <div style={{ marginTop: 8 }}><Badge kind={mapping.status}>{mapping.status}</Badge></div>
          <div className="tile-foot">
            {mapping.reviewedBy ? `${mapping.reviewedBy} · ${fmtDate(mapping.reviewedAt)}` : 'Not yet reviewed'}
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Match method</div>
          <div style={{ marginTop: 8 }}><Badge kind="neutral">{mapping.matchMethod}</Badge></div>
          <div className="tile-foot">Batch #{mapping.batchId ?? '-'}</div>
        </div>
      </div>

      <h3 style={{ marginBottom: 8 }}>Attribute comparison</h3>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="cmp">
          <div className="cmp-key">Attribute</div>
          <div className="cmp-head">{mapping.insurer.code} record</div>
          <div className="cmp-head">GIIB vehicle</div>
          {rows.map(([label, a, b]) => {
            const differs = String(a ?? '').toLowerCase() !== String(b ?? '').toLowerCase();
            return (
              <div key={label} style={{ display: 'contents' }}>
                <div className="cmp-key">{label}</div>
                <div className={differs ? 'diff' : ''}>{a ?? '-'}</div>
                <div className={differs ? 'diff' : ''}>{b ?? '-'}</div>
              </div>
            );
          })}
          <div className="cmp-key">Identifier</div>
          <div>
            <span className="mono">{ic.code}</span>
            <div className="prov">{ic.codeColumn ? `from ${ic.codeColumn} in the insurer's file` : 'insurer code'}</div>
          </div>
          <div>
            <span className="mono">{giib?.universalKey || '-'}</span>
            <div className="prov">universal key minted by MMV Mapper</div>
          </div>
        </div>
      </div>

      {mapping.breakdown && (
        <>
          <h3 style={{ marginBottom: 8 }}>Score breakdown</h3>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Attribute</th>
                    <th>Weight</th>
                    <th style={{ minWidth: 140 }}>Score</th>
                    <th>Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(mapping.breakdown).map(([key, part]) => (
                    <tr key={key}>
                      <td>
                        {ATTRIBUTE_LABELS[key] || key}
                        {!part.comparable && <div className="prov">not comparable - weight redistributed</div>}
                      </td>
                      <td className="mono">{(Number(part.weight) * 100).toFixed(0)}%</td>
                      <td>
                        {part.score === null ? <span className="prov">-</span>
                          : <ConfidenceMeter value={Number(part.score) * 100} band={Number(part.score) >= 0.9 ? 'high' : Number(part.score) >= 0.7 ? 'medium' : 'low'} />}
                      </td>
                      <td className="mono">
                        {part.score === null ? '-' : `${(Number(part.score) * Number(part.weight) * 100).toFixed(1)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {mapping.candidates?.length > 1 && (
        <>
          <h3 style={{ marginBottom: 8 }}>Other candidates considered</h3>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>#</th><th>Vehicle</th><th>Universal key</th><th style={{ minWidth: 140 }}>Confidence</th></tr>
                </thead>
                <tbody>
                  {mapping.candidates.map((c) => (
                    <tr key={c.vehicle.id}>
                      <td className="mono">{c.rank}</td>
                      <td>
                        {vehicleLabel(c.vehicle)}
                        {c.vehicle.cubicCapacity ? <div className="prov">{c.vehicle.cubicCapacity} cc</div> : null}
                      </td>
                      <td className="mono">{c.vehicle.universalKey}</td>
                      <td><ConfidenceMeter value={c.confidence} band={c.confidence >= 90 ? 'high' : c.confidence >= 70 ? 'medium' : 'low'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {canReview && (
        <div className="field">
          <label htmlFor="comment">Reviewer comment</label>
          <textarea
            id="comment" value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Required when rejecting, or when overriding a failed band check."
          />
          <div className="field-hint">Comments are stored on the mapping and in the audit trail.</div>
        </div>
      )}

      {mapping.history?.length > 1 && (
        <>
          <h3 style={{ margin: '16px 0 8px' }}>Decision history</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>When</th><th>Status</th><th>Vehicle</th><th>By</th><th>Comment</th></tr>
              </thead>
              <tbody>
                {mapping.history.map((h) => (
                  <tr key={h.id}>
                    <td>{fmtDate(h.at)}</td>
                    <td><Badge kind={h.status}>{h.status}</Badge></td>
                    <td className="mono">{h.universalKey || '-'}</td>
                    <td>{h.reviewedBy || <span className="prov">engine</span>}</td>
                    <td>{h.comment || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Drawer>
  );
}

/* ------------------------------- remap ------------------------------- */

function RemapModal({ mapping, onClose, onSubmit, busy }) {
  const ic = mapping.icRecord;
  const [search, setSearch] = useState([ic.make, ic.model].filter(Boolean).join(' '));
  const [ccFrom, setCcFrom] = useState(ic.cubicCapacity ? Math.floor(ic.cubicCapacity * 0.9) : '');
  const [ccTo, setCcTo] = useState(ic.cubicCapacity ? Math.ceil(ic.cubicCapacity * 1.1) : '');
  const [fuelType, setFuelType] = useState(ic.fuelType || '');
  const [chosen, setChosen] = useState(null);
  const [comment, setComment] = useState('');

  const results = useQuery({
    queryKey: ['vehicleSearch', search, ccFrom, ccTo, fuelType],
    queryFn: () => api.listVehicles({ search, ccFrom, ccTo, fuelType, limit: 20 }).then((r) => r.data),
    enabled: search.trim().length >= 2,
  });

  const chosenLabel = useMemo(() => (chosen ? vehicleLabel(chosen) : null), [chosen]);

  return (
    <Modal
      title="Remap to a different GIIB vehicle"
      onClose={onClose}
      wide
      footer={(
        <>
          <button
            className="btn btn-primary"
            disabled={!chosen || busy}
            onClick={() => onSubmit({ giibVehicleId: chosen.id, comment: comment || undefined })}
          >
            {busy ? 'Saving...' : `Remap to ${chosenLabel || '...'}`}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <Notice tone="info" title="Insurer record">
        {[ic.make, ic.model, ic.variant].filter(Boolean).join(' ')} — {mapping.insurer.code} code {ic.code}
        {ic.cubicCapacity ? ` — ${ic.cubicCapacity} cc` : ''}
      </Notice>

      <div className="grid-3">
        <div className="field">
          <label htmlFor="r-search">Search the GIIB master</label>
          <input id="r-search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="r-ccfrom">cc from</label>
          <input id="r-ccfrom" type="number" value={ccFrom} onChange={(e) => setCcFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="r-ccto">cc to</label>
          <input id="r-ccto" type="number" value={ccTo} onChange={(e) => setCcTo(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="r-fuel">Fuel type</label>
          <input id="r-fuel" value={fuelType} onChange={(e) => setFuelType(e.target.value)} placeholder="any" />
        </div>
      </div>

      {results.isLoading && <Loading rows={4} />}
      {results.data && (
        <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr><th /><th>Vehicle</th><th>Universal key</th><th className="num">cc</th><th>Fuel</th><th className="num">Linked ICs</th></tr>
            </thead>
            <tbody>
              {results.data.map((v) => (
                <tr
                  key={v.id}
                  className={`clickable ${chosen?.id === v.id ? 'selected' : ''}`}
                  onClick={() => setChosen(v)}
                >
                  <td><input type="radio" checked={chosen?.id === v.id} onChange={() => setChosen(v)} aria-label={`Select ${vehicleLabel(v)}`} /></td>
                  <td>{vehicleLabel(v)}</td>
                  <td className="mono">{v.universalKey}</td>
                  <td className="num">{v.cubicCapacity ?? '-'}</td>
                  <td>{v.fuelType || '-'}</td>
                  <td className="num">{v.linkedInsurers ?? '-'}</td>
                </tr>
              ))}
              {results.data.length === 0 && (
                <tr><td colSpan={6}><Empty title="No vehicle matches those filters">Widen the cc range, or add the vehicle to the GIIB master first.</Empty></td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="field" style={{ marginTop: 14 }}>
        <label htmlFor="r-comment">Why is this the correct vehicle?</label>
        <textarea id="r-comment" value={comment} onChange={(e) => setComment(e.target.value)} />
        <div className="field-hint">Recorded against the mapping and in the audit trail.</div>
      </div>
    </Modal>
  );
}

/* --------------------------- bulk approve ---------------------------- */

function BulkApproveModal({ insurers, onClose, onDone }) {
  const toast = useToast();
  const [minConfidence, setMinConfidence] = useState(95);
  const [insurerId, setInsurerId] = useState('');
  const [result, setResult] = useState(null);

  const mutation = useMutation({
    mutationFn: () => api.bulkApprove({
      minConfidence: Number(minConfidence),
      insurerId: insurerId ? Number(insurerId) : undefined,
    }).then((r) => r.data),
    onSuccess: (data) => {
      setResult(data);
      toast.success(`${data.approved} mapping(s) approved`);
      onDone();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Modal
      title="Bulk approve pending mappings"
      onClose={onClose}
      footer={(
        <>
          <button className="btn btn-primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Approving...' : 'Approve matching rows'}
          </button>
          <button className="btn" onClick={onClose}>Close</button>
        </>
      )}
    >
      <Notice tone="warn" title="Rows failing the premium band check are always skipped">
        Bulk approval only touches pending rows that have a matched vehicle and a band check of pass or
        not-applicable. Everything else stays in the queue for a person to look at.
      </Notice>

      <div className="grid-2">
        <div className="field">
          <label htmlFor="b-conf">Minimum confidence</label>
          <input id="b-conf" type="number" min="50" max="100" step="0.5" value={minConfidence} onChange={(e) => setMinConfidence(e.target.value)} />
          <div className="field-hint">95 or above is the recommended starting point for UAT.</div>
        </div>
        <div className="field">
          <label htmlFor="b-insurer">Limit to insurer</label>
          <select id="b-insurer" value={insurerId} onChange={(e) => setInsurerId(e.target.value)}>
            <option value="">All insurers</option>
            {insurers.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
      </div>

      {result && (
        <Notice tone="ok" title={`${result.approved} approved, ${result.skipped} skipped`}>
          Skipped rows either fail the band check, have no matched vehicle, or were already decided.
        </Notice>
      )}
    </Modal>
  );
}
